import axios from 'axios';
import { getAuthToken } from '../utils/authUtils';
import { getApiBaseUrl } from '../utils/apiConfig';

const API_URL = getApiBaseUrl();

// Create axios instance with auth header
const authAxios = axios.create({
  baseURL: `${API_URL}/weekly-availability`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add authorization header to every request
authAxios.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
authAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Weekly Availability Service
 * Handles all API calls related to professional weekly availability templates
 */
const weeklyAvailabilityService = {
  /**
   * Get the current professional's weekly availability template
   * @returns {Promise} API response with weekly template
   */
  getWeeklyTemplate: async () => {
    try {
      const response = await authAxios.get('/template');
      return response.data;
    } catch (error) {
      console.error('Error fetching weekly template:', error);
      throw error;
    }
  },

  /**
   * Save or update the weekly availability template
   * @param {Object} templateData - Weekly template data
   * @returns {Promise} API response
   */
  saveWeeklyTemplate: async (templateData) => {
    try {
      console.log('📤 Saving weekly template with data:', JSON.stringify(templateData, null, 2));
      console.log('📤 Request URL:', `${API_URL}/weekly-availability/template`);
      
      const response = await authAxios.post('/template', templateData);
      console.log('✅ Weekly template save response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error saving weekly template:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL
        }
      });
      throw error;
    }
  },

  /**
   * Update an existing weekly template
   * @param {string} templateId - Template ID
   * @param {Object} templateData - Updated template data
   * @returns {Promise} API response
   */
  updateWeeklyTemplate: async (templateId, templateData) => {
    try {
      const response = await authAxios.put(`/template/${templateId}`, templateData);
      return response.data;
    } catch (error) {
      console.error('Error updating weekly template:', error);
      throw error;
    }
  },

  /**
   * Delete a weekly template
   * @param {string} templateId - Template ID
   * @returns {Promise} API response
   */
  deleteWeeklyTemplate: async (templateId) => {
    try {
      const response = await authAxios.delete(`/template/${templateId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting weekly template:', error);
      throw error;
    }
  },

  /**
   * Generate actual time slots from weekly template for a date range
   * @param {Object} params - Generation parameters
   * @param {string} params.startDate - Start date (YYYY-MM-DD)
   * @param {string} params.endDate - End date (YYYY-MM-DD)
   * @param {string} params.templateId - Template ID (optional, uses active template if not provided)
   * @returns {Promise} API response with generated time slots
   */
  generateTimeSlotsFromTemplate: async (params) => {
    try {
      const response = await authAxios.post('/generate-slots', params);
      return response.data;
    } catch (error) {
      console.error('Error generating time slots from template:', error);
      throw error;
    }
  },

  /**
   * Get all weekly templates for the current professional
   * @returns {Promise} API response with templates array
   */
  getAllTemplates: async () => {
    try {
      const response = await authAxios.get('/templates');
      return response.data;
    } catch (error) {
      console.error('Error fetching all templates:', error);
      throw error;
    }
  },

  /**
   * Set a template as active
   * @param {string} templateId - Template ID to activate
   * @returns {Promise} API response
   */
  setActiveTemplate: async (templateId) => {
    try {
      const response = await authAxios.put(`/template/${templateId}/activate`);
      return response.data;
    } catch (error) {
      console.error('Error activating template:', error);
      throw error;
    }
  },

  /**
   * Get template usage statistics
   * @param {string} templateId - Template ID
   * @returns {Promise} API response with statistics
   */
  getTemplateStats: async (templateId) => {
    try {
      const response = await authAxios.get(`/template/${templateId}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching template stats:', error);
      throw error;
    }
  },

  /**
   * Preview time slots that would be generated from template
   * @param {Object} templateData - Template data to preview
   * @param {string} startDate - Preview start date (YYYY-MM-DD)
   * @param {string} endDate - Preview end date (YYYY-MM-DD)
   * @returns {Promise} API response with preview slots
   */
  previewTemplateSlots: async (templateData, startDate, endDate) => {
    try {
      const response = await authAxios.post('/preview-slots', {
        template: templateData,
        startDate,
        endDate
      });
      return response.data;
    } catch (error) {
      console.error('Error previewing template slots:', error);
      throw error;
    }
  },

  /**
   * Helper: Convert template schedule to time slot format
   * @param {Array} schedule - Weekly schedule array
   * @param {Date} weekStart - Start of the week
   * @returns {Array} Formatted time slots
   */
  convertScheduleToTimeSlots: (schedule, weekStart) => {
    const timeSlots = [];
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    schedule.forEach(slot => {
      const dayIndex = dayNames.indexOf(slot.day.toLowerCase());
      if (dayIndex === -1) return;

      const slotDate = new Date(weekStart);
      slotDate.setDate(slotDate.getDate() + dayIndex);

      timeSlots.push({
        date: slotDate.toISOString().split('T')[0],
        startTime: slot.startTime,
        endTime: slot.endTime,
        notes: slot.notes || '',
        rateOverride: slot.rateOverride || null,
        timezone: 'UTC'
      });
    });

    return timeSlots;
  },

  /**
   * Helper: Validate weekly template structure
   * @param {Object} template - Template to validate
   * @returns {Object} Validation result { isValid, errors }
   */
  validateTemplate: (template) => {
    const errors = [];

    if (!template.name || template.name.trim() === '') {
      errors.push('Template name is required');
    }

    if (!Array.isArray(template.schedule)) {
      errors.push('Schedule must be an array');
    } else {
      template.schedule.forEach((slot, index) => {
        if (!slot.day || !['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].includes(slot.day.toLowerCase())) {
          errors.push(`Invalid day in slot ${index + 1}`);
        }

        if (!slot.startTime || !/^([01]\d|2[0-3]):([0-5]\d)$/.test(slot.startTime)) {
          errors.push(`Invalid start time in slot ${index + 1}`);
        }

        if (!slot.endTime || !/^([01]\d|2[0-3]):([0-5]\d)$/.test(slot.endTime)) {
          errors.push(`Invalid end time in slot ${index + 1}`);
        }

        if (slot.startTime && slot.endTime && slot.startTime >= slot.endTime) {
          errors.push(`End time must be after start time in slot ${index + 1}`);
        }

        if (slot.rateOverride !== null && slot.rateOverride !== undefined) {
          if (typeof slot.rateOverride !== 'number' || slot.rateOverride < 0) {
            errors.push(`Invalid rate override in slot ${index + 1}`);
          }
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
};

export default weeklyAvailabilityService;